<template>
  <div
    class="form-element"
  >
    <FormLabel :label="label" />
    <div class="d-flex">
      <FormYesNo
        class="ml-1"
        :value="value.useFilter === 'true' || value.useFilter === true"
        @input="$event => { updateFilter('useFilter', $event); saveFilter() }"
      />
      <AppButton
        variant="dark"
        class="ml-2 text-dark"
        @click="showFilterModal = true"
      >
        <AppIcon
          name="settings"
          class="mr-2"
        />
        Settings
      </AppButton>
    </div>
    <AppModal
      v-if="showFilterModal"
      ref="modal"
      :stacked="true"
      useDimness
      @close="closeFilterModal"
    >
      <template #modal-header>
        <AppHeading>
          {{ $localize('program') | capitalize }} Changes Filter
        </AppHeading>
      </template>
      <template #modal-body>
        <div style="height: 30vh">
          <FormSelect
            :config="{
              options: seeableDateQuestionsArray,
              trackBy: 'questionId',
              multiple: false,
            }"
            :searchable="true"
            :customLabel="field => field.label"
            label="Date Of Last Review Field"
            placeholder="Select field"
            :value="valueToBeSaved.dateOfLastReviewField"
            @input="updateFilter('dateOfLastReviewField', $event)"
          />

          <FormSelect
            class="pt-2"
            :config="{
              options: seeableCreditsQuestionsArray,
              trackBy: 'questionId',
              multiple: false,
            }"
            :searchable="true"
            :customLabel="field => field.label"
            label="Credit Field"
            placeholder="Select field"
            :value="valueToBeSaved.creditField"
            @input="updateFilter('creditField', $event)"
          />

          <FormNumber
            :value="valueToBeSaved.percentage"
            label="% Change Minimum"
            class="pt-2 mb-2 pb-5"
            :config="{
              min: 0,
              max: 100,
              step: 1,
            }"
            @input="updateFilter('percentage', $event)"
          />
        </div>
      </template>
      <template #modal-footer>
        <AppButton
          :disabled="!isSavingAllowed"
          @click="saveFilter"
        >
          <AppIcon
            name="save"
            class="mr-2"
          />
          Save
        </AppButton>
      </template>
    </AppModal>
  </div>
</template>

<script>
import FormYesNo from './FormYesNo'
import FormLabel from './FormLabel'
import FormNumber from './FormNumber'
import FormSelect from './FormSelect'
import useDocumentTemplate from '@/components/composables/useDocumentTemplate'
import { computed, ref } from '@vue/composition-api'
import { userHasAnyAllowedRole } from '@shared/permissions/validators'
import { isEmpty } from '@shared/utils/isEmpty'
import _isEqual from 'lodash/isEqual'

export default {
  name: 'FormProgramChanges',
  components: { FormYesNo, FormLabel, FormNumber, FormSelect },
  props: {
    value: {
      type: Object,
      default () {
        return {
          useFilter: false,
          percentage: 25,
          // MYzUS is a custom field for CUNY (Last Reg Action)
          // https://www.notion.so/Program-Changes-Filter-d2a55f2471e24e879e1504e79c37cf98?d=47228025e5c1422ba3a4e0f8dd173ebe#e405553393ca42cb8823d2e266b9e567
          dateOfLastReviewField: 'MYzUS',
          customDateOfLastReviewField: true,
          creditField: 'credits',
        }
      },
    },
    label: {
      type: String,
    },
  },
  setup (props, { root }) {
    const { baseQuestionsArray: baseProgramQuestionsArray, loadBaseTemplate: loadBaseProgramTemplate } = useDocumentTemplate({ type: ref('program') })
    const { baseQuestionsArray: baseCourseQuestionsArray, loadBaseTemplate: loadBaseCourseTemplate } = useDocumentTemplate({ type: ref('course') })

    const user = root.$store.getters.user

    const seeableDateQuestionsArray = computed(() =>
      baseProgramQuestionsArray.value.filter(
        question => (!question.rolesAllowedToSee || userHasAnyAllowedRole(user, null, question.rolesAllowedToSee)) &&
                  question.questionType && question.questionType.toLowerCase().includes('date')
      )
    )

    const seeableCreditsQuestionsArray = computed(() =>
      baseCourseQuestionsArray.value.filter(
        question => (!question.rolesAllowedToSee || userHasAnyAllowedRole(user, null, question.rolesAllowedToSee)) &&
                  question.questionType && question.questionType.toLowerCase().includes('credits')
      )
    )

    return {
      baseProgramQuestionsArray,
      baseCourseQuestionsArray,
      seeableDateQuestionsArray,
      seeableCreditsQuestionsArray,
      loadBaseProgramTemplate,
      loadBaseCourseTemplate,
      showFilterModal: false,
      valueToBeSaved: {},
    }
  },
  computed: {
    isSavingAllowed () {
      return this.valueToBeSaved.dateOfLastReviewField &&
             parseFloat(this.valueToBeSaved.percentage) >= 0 &&
             parseFloat(this.valueToBeSaved.percentage) <= 100 &&
             !_isEqual(this.value, this.valueToBeSaved)
    },
  },
  watch: {
    value (value) {
      if (!_isEqual(value, this.valueToBeSaved)) {
        this.valueToBeSaved = this.value
      }
    },
  },
  created () {
    this.valueToBeSaved = this.value
    if (isEmpty(this.baseProgramQuestionsArray) && this.loadBaseTemplate) {
      this.loadBaseTemplate()
    }
    if (isEmpty(this.baseCourseQuestionsArray) && this.loadBaseCourseTemplate) {
      this.loadBaseCourseTemplate()
    }
  },
  methods: {
    closeFilterModal () {
      this.$emit('close')
      this.showFilterModal = false
      this.valueToBeSaved = this.value
    },
    updateFilter (key, value) {
      this.valueToBeSaved = { ...this.valueToBeSaved, [key]: value }
    },
    saveFilter () {
      this.$emit('input', this.valueToBeSaved)
      this.closeFilterModal()
    },
  },
}
</script>
